import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Api from '../Api/Api.json';
import {
  VictoryAxis,
  VictoryChart,
  VictoryBar,
  VictoryLabel,
} from 'victory';

const RecordIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const getTheme = (variant) => {
  if (variant === 'owner') {
    return {
      sectionCard: 'bg-white rounded-lg shadow-md border border-gray-200',
      innerCard: 'bg-gray-50 border border-gray-200',
      headerGradient: 'bg-gradient-to-r from-blue-500 to-indigo-600',
      text: 'text-gray-800',
      mutedText: 'text-gray-600',
      dropdownBtn: 'bg-white border-gray-300 hover:bg-gray-50 text-gray-800',
      dropdownMenu: 'bg-white border-gray-200',
      dropdownItem: 'text-gray-800 hover:bg-gray-100',
      dropdownItemActive: 'bg-blue-50 text-blue-600',
      errorCard: 'bg-red-50 border-red-200 text-red-800',
      axisStroke: '#e5e7eb',
      tickFill: '#6b7280',
      chartTitle: 'text-gray-800',
    };
  }
  return {
    sectionCard: 'bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-lg overflow-hidden',
    innerCard: 'bg-[#161B22] border border-[#30363D]',
    headerGradient: '',
    text: 'text-white',
    mutedText: 'text-white',
    dropdownBtn: 'bg-[#161B22] border-[#30363D] hover:bg-[#21262D] text-white',
    dropdownMenu: 'bg-[#161B22] border-[#30363D]',
    dropdownItem: 'text-white hover:bg-[#21262D]',
    dropdownItemActive: 'bg-[#21262D] text-[#58A6FF]',
    errorCard: 'bg-red-50 border-red-200 text-red-800',
    axisStroke: '#8B949E',
    tickFill: '#F0F6FC',
    chartTitle: 'text-white',
  };
};

const ExerciseRecordsContent = ({ patientId, variant = 'default' }) => {
  const theme = getTheme(variant);
  const isOwner = variant === 'owner';

  const [exerciseList, setExerciseList] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [exerciseError, setExerciseError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchExerciseList = async () => {
      if (!patientId) return;

      setIsLoadingList(true);
      setExerciseError(null);

      try {
        const headers = { headers: { 'Content-Type': 'application/json' } };
        const url = Api.EXERCISE_SESSION_DATA_API;
        const requestData = { task: 'get_exercise_session_list', patient_id: patientId };
        const response = await axios.post(url, requestData, headers);

        let responseData = response.data;
        if (responseData?.body) responseData = responseData.body;
        if (responseData?.data) responseData = responseData.data;

        if (responseData && Array.isArray(responseData)) {
          const mappedData = responseData.map((item) => {
            let timestamp;
            if (item.timestamp) {
              timestamp = typeof item.timestamp === 'string'
                ? new Date(item.timestamp)
                : new Date(item.timestamp * 1000);
            } else if (item.datetime) {
              timestamp = new Date(item.datetime);
            } else {
              timestamp = new Date();
            }
            return {
              datetime: item.datetime || timestamp.toISOString(),
              timestamp,
              originalTimestamp: item.timestamp || item.datetime,
            };
          });
          const sortedData = mappedData.sort((a, b) => b.timestamp - a.timestamp);
          setExerciseList(sortedData);
          if (sortedData.length > 0) setSelectedExercise(sortedData[0]);
        } else {
          setExerciseList([]);
        }
      } catch (err) {
        console.error('Error fetching exercise session list:', err);
        setExerciseError('Failed to load exercise sessions. Please try again.');
        setExerciseList([]);
      } finally {
        setIsLoadingList(false);
      }
    };

    fetchExerciseList();
  }, [patientId]);

  useEffect(() => {
    const fetchExerciseDetails = async () => {
      if (!patientId || !selectedExercise || !selectedExercise.originalTimestamp) return;

      setIsLoadingDetails(true);
      setExerciseError(null);

      try {
        const headers = { headers: { 'Content-Type': 'application/json' } };
        const url = Api.EXERCISE_SESSION_DATA_API;
        const timestampForApi = typeof selectedExercise.originalTimestamp === 'string'
          ? selectedExercise.originalTimestamp
          : selectedExercise.originalTimestamp instanceof Date
            ? selectedExercise.originalTimestamp.toISOString()
            : String(selectedExercise.originalTimestamp);

        const requestData = {
          task: 'get_exercise_session_data',
          patient_id: patientId,
          timestamp: timestampForApi,
        };

        const response = await axios.post(url, requestData, headers);
        let responseData = response.data;
        if (responseData?.body) responseData = responseData.body;
        if (responseData?.data) responseData = responseData.data;
        if (Array.isArray(responseData) && responseData.length > 0) {
          responseData = responseData[0];
        }

        if (responseData) {
          let parsedZoneSummary = null;
          let parsedZoneMap = null;

          if (responseData.zone_summary) {
            parsedZoneSummary = typeof responseData.zone_summary === 'string'
              ? JSON.parse(responseData.zone_summary)
              : responseData.zone_summary;
          }
          if (responseData.zone_map) {
            parsedZoneMap = typeof responseData.zone_map === 'string'
              ? JSON.parse(responseData.zone_map)
              : responseData.zone_map;
          }

          setSelectedExercise({
            ...selectedExercise,
            heartrate_min: responseData.heartrate_min ?? selectedExercise.heartrate_min,
            heartrate_max: responseData.heartrate_max ?? selectedExercise.heartrate_max,
            heartrate_avg: responseData.heartrate_avg ?? selectedExercise.heartrate_avg,
            total_time: responseData.total_time ?? selectedExercise.total_time,
            total_steps: responseData.total_steps ?? selectedExercise.total_steps,
            total_distance: responseData.total_distance ?? selectedExercise.total_distance,
            total_calories: responseData.total_calories ?? selectedExercise.total_calories,
            avg_speed: responseData.avg_speed ?? selectedExercise.avg_speed,
            vo2max: responseData.vo2max ?? selectedExercise.vo2max,
            cardio_recovery_score: responseData.cardio_recovery_score ?? responseData.cardio_recovery ?? selectedExercise.cardio_recovery_score,
            zone_summary: parsedZoneSummary,
            zone_map: parsedZoneMap,
          });
        }
      } catch (err) {
        console.error('Error fetching exercise session details:', err);
        setExerciseError('Failed to load exercise session details. Please try again.');
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchExerciseDetails();
  }, [patientId, selectedExercise?.originalTimestamp]);

  const handleExerciseSelect = (exercise) => {
    setSelectedExercise(exercise);
    setIsDropdownOpen(false);
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
  };

  const formatDistance = (km) => {
    if (!km) return '0 m';
    return `${Math.round(km * 1)} m`;
  };

  const formatSpeed = (kmh) => {
    if (!kmh) return '0 km/h';
    return `${kmh.toFixed(1)} km/h`;
  };

  const zoneMapChartData = useMemo(() => {
    if (!selectedExercise?.zone_map || !Array.isArray(selectedExercise.zone_map)) return [];
    return selectedExercise.zone_map.map((entry, index) => ({
      x: (index * 30 + 15) / 60,
      y: entry.zone === -1 ? -1 : (entry.zone >= 0 && entry.zone <= 5 ? entry.zone : 0),
      avgHeartrate: entry.avg_heartrate || null,
    }));
  }, [selectedExercise]);

  const zoneSummaryChartData = useMemo(() => {
    if (!selectedExercise?.zone_summary || typeof selectedExercise.zone_summary !== 'object') return [];
    const zoneCounts = [0, 0, 0, 0, 0, 0];
    Object.keys(selectedExercise.zone_summary).forEach((zoneKey) => {
      const zone = parseInt(zoneKey, 10);
      const minutes = selectedExercise.zone_summary[zoneKey];
      if (zone >= 0 && zone <= 5 && typeof minutes === 'number') {
        zoneCounts[zone] = minutes;
      }
    });
    return zoneCounts.map((minutes, zone) => ({ x: zone, y: minutes, label: `${zone}` }));
  }, [selectedExercise]);

  const sectionWrapperClass = isOwner
    ? theme.sectionCard
    : 'bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-lg overflow-hidden';

  return (
    <div className="mb-6">
      <div className={sectionWrapperClass}>
        <div className={`p-6 text-white ${isOwner ? theme.headerGradient + ' rounded-t-lg' : ''}`}>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <RecordIcon />
            </div>
            <div>
              <h3 className="text-xl font-semibold">Exercise Record</h3>
              <p className="text-sm opacity-90">View exercise history</p>
            </div>
          </div>
        </div>

        <div className={`p-6 ${theme.innerCard} ${isOwner ? 'rounded-b-lg' : ''}`}>
          {exerciseError && (
            <div className={`border rounded-lg p-4 mb-6 ${theme.errorCard}`}>
              {exerciseError}
            </div>
          )}

          <div className="mb-6">
            <h4 className={`text-lg font-semibold mb-4 ${theme.text}`}>Exercise Sessions</h4>
            {isLoadingList ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                <span className={`ml-3 ${theme.mutedText}`}>Loading exercise sessions...</span>
              </div>
            ) : exerciseList.length > 0 ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`w-full px-4 py-3 text-left border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors flex items-center justify-between ${theme.dropdownBtn}`}
                >
                  <span className="text-sm">
                    {selectedExercise ? formatDateTime(selectedExercise.timestamp) : 'Select an exercise session'}
                  </span>
                  <svg className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)} />
                    <div className={`absolute z-20 mt-1 w-full border rounded-md shadow-lg max-h-60 overflow-auto ${theme.dropdownMenu}`}>
                      {exerciseList.map((exercise, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleExerciseSelect(exercise)}
                          className={`w-full px-4 py-3 text-left text-sm transition-colors ${
                            selectedExercise?.originalTimestamp === exercise.originalTimestamp
                              ? theme.dropdownItemActive
                              : theme.dropdownItem
                          }`}
                        >
                          {formatDateTime(exercise.timestamp)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : patientId ? (
              <p className={theme.mutedText}>No exercise data available</p>
            ) : (
              <p className={theme.mutedText}>No patient data available</p>
            )}
          </div>

          {selectedExercise && (
            <div className={`rounded-lg shadow-md p-6 mb-6 ${isOwner ? 'bg-white border border-gray-200' : 'bg-[#161B22] border border-[#30363D]'}`}>
              <div className="flex items-center justify-between mb-4">
                <h4 className={`text-lg font-semibold ${theme.text}`}>Exercise Details</h4>
                {isLoadingDetails && (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                    <span className={`ml-2 text-sm ${theme.mutedText}`}>Loading...</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  ['Timestamp', formatDateTime(selectedExercise.timestamp)],
                  ['Heart Rate Min', `${selectedExercise.heartrate_min ?? 'N/A'} bpm`],
                  ['Heart Rate Max', `${selectedExercise.heartrate_max ?? 'N/A'} bpm`],
                  ['Heart Rate Avg', `${selectedExercise.heartrate_avg ?? 'N/A'} bpm`],
                  ['Total Time', formatDuration(selectedExercise.total_time)],
                  ['Total Steps', selectedExercise.total_steps?.toLocaleString() || 'N/A'],
                  ['Total Distance', formatDistance(selectedExercise.total_distance)],
                  ['Total Calories', selectedExercise.total_calories || 'N/A'],
                  ['Avg Speed', formatSpeed(selectedExercise.avg_speed)],
                  ['VO2 Max', `${selectedExercise.vo2max ?? 'N/A'} ml/kg/min`],
                  ['Cardio Recovery Score', `${selectedExercise.cardio_recovery_score ?? 'N/A'} bpm`],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div className={`text-xs mb-1 font-bold ${theme.mutedText}`}>{label}</div>
                    <div className={`text-sm font-medium ${theme.text}`}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-full mt-4 mb-0" style={{ minHeight: '390px' }}>
        <h4 className={`text-lg font-semibold mb-4 ${theme.chartTitle}`}>Heart Rate Zone with Time (Minutes)</h4>
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <VictoryChart
            domainPadding={{ x: [10, 10] }}
            padding={{ top: 40, bottom: 80, left: 60, right: 20 }}
            width={zoneMapChartData.length > 0 ? Math.max(600, Math.max(zoneMapChartData.length * 40, 400)) : 600}
            height={390}
            domain={{
              x: zoneMapChartData.length > 0 ? [0, (zoneMapChartData.length * 30) / 60] : [0, 1],
              y: [-1, 5],
            }}
            style={{ parent: { width: '100%', background: 'transparent' } }}
          >
            <VictoryAxis
              style={{
                axis: { stroke: theme.axisStroke },
                tickLabels: { fontSize: isOwner ? 12 : 40, fill: theme.tickFill, angle: -45, textAnchor: 'end' },
                grid: null,
              }}
              tickFormat={(t) => {
                if (t % 1 === 0) return `${t}m`;
                const mins = Math.floor(t);
                const secs = Math.round((t - mins) * 60);
                return mins === 0 ? `${secs}s` : `${mins}m ${secs}s`;
              }}
            />
            <VictoryAxis
              dependentAxis
              tickValues={[-1, 0, 1, 2, 3, 4, 5]}
              tickFormat={(t) => (t === -1 ? 'ND' : `${t}`)}
              domain={[-1, 5]}
              style={{
                axis: { stroke: theme.axisStroke },
                tickLabels: { fontSize: isOwner ? 12 : 30, fill: theme.tickFill },
                grid: { stroke: theme.axisStroke, strokeWidth: 0.5 },
              }}
            />
            <VictoryBar
              data={zoneMapChartData}
              style={{ data: { fill: '#58A6FF', stroke: '#58A6FF', strokeWidth: 0.5 } }}
              barWidth={30}
            />
          </VictoryChart>
        </div>
      </div>

      <div className="w-full mt-0 mb-6" style={{ minHeight: '300px' }}>
        <h4 className={`text-lg font-semibold mb-4 ${theme.chartTitle}`}>Time Spent in Each Zone</h4>
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <VictoryChart
            domainPadding={{ x: 20 }}
            padding={{ top: 0, bottom: 60, left: 60, right: 20 }}
            width={600}
            height={300}
            domain={{ x: [-0.2, 5.5] }}
            style={{ parent: { width: '100%', background: 'transparent' } }}
          >
            <VictoryAxis
              tickValues={[0, 1, 2, 3, 4, 5]}
              tickFormat={(t) => `${t}`}
              style={{
                axis: { stroke: theme.axisStroke },
                tickLabels: { fontSize: isOwner ? 14 : 40, fill: theme.tickFill },
                grid: null,
              }}
            />
            <VictoryAxis
              dependentAxis
              style={{
                axis: { stroke: theme.axisStroke },
                tickLabels: { fontSize: 0, fill: 'transparent' },
                grid: { stroke: theme.axisStroke, strokeWidth: 0.5 },
              }}
            />
            <VictoryBar
              data={zoneSummaryChartData}
              labels={({ datum }) => (datum.y > 0 ? `${datum.y} min` : '')}
              labelComponent={<VictoryLabel dy={-5} style={{ fontSize: isOwner ? 12 : 22, fill: theme.tickFill, fontWeight: 'bold' }} />}
              style={{
                data: { fill: '#58A6FF', stroke: '#58A6FF', strokeWidth: 1 },
              }}
              barWidth={50}
            />
          </VictoryChart>
        </div>
      </div>
    </div>
  );
};

export default ExerciseRecordsContent;
